import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { prepareProduct } from './product-fns'  
import { deepEqual } from '@/js/services/Utils'
import { Product } from '@/js/services/RogueTypes'
import type { IRogue } from '@/js/services/RogueTypes'

export const useProductStore = defineStore('products', () => {
  // Promises to load single products (micropackage, pricing, etc)
  const loadShallowProduct: { [productId: number]: Promise<Product | undefined> } = {}

  // promises to load products including their children (RHPA, PDP, etc)
  const loadFullProduct: { [key: number]: Promise<any> } = {}

  const products = ref<{ [productId: number | string]: Product }>({})
  const errors = ref<{ [productId: number]: boolean }>({})
  const byUrl = ref<{ [url: string]: number | null }>({})

  function setProductData(productData: Product) {
    products.value[productData.productId] = productData
    if (productData.visibility !== 1 && productData.url) {
      byUrl.value[productData.url.toLowerCase()] = productData.productId
    }
  }

  function setIdForUrl(url: string, id: number) {
    if (!byUrl.value[url]) {
      byUrl.value[url] = id
    }
  }

  // If a route fails, we can wipe it from our urlMap
  function eraseIdForUrl(url: string) {
    byUrl.value[url] = null
  }

  // Sync the related state of a set of related products
  // This will allow users to switch between them without jumping the Rogue Related icons
  function syncRelated(productId: number, relatedIds: number[]) {
    const product = products.value[productId]
    if (product) {
      product.relatedIds = relatedIds.slice(0)
    }
  }

  /**
   * Load a product by SKU or productId
   */
  async function load(productId: number | string, rogue: IRogue) {
    /**
     * Sometimes we have to load a product by SKU (e.g. for Gymbuilder third party API, where they send us only SKUs)
     * We always prefer to load by productId when possible
     */
    const loadBySKU = typeof productId === 'string' && Number.isNaN(parseInt(productId, 10))

    if (loadBySKU) {
      const existing = Object.values(products.value).find(p => p.sku === productId)
      if (existing) {
        if (!products.value[productId]) {
          products.value[productId] = existing
        }

        return existing
      }
    } else if (products.value[productId as number]) {
      return products.value[productId as number]
    }

    /** We dont want to re-load products that errored out (invalid, etc)
     * Also, we want to return synchronously - so if the SSR errored, the client will know "This isn't available"
     * and not try to load the product all over again
     */
    if (errors.value[productId as number]) {
      return errors.value[productId as number]
    }

    /**
     * Check if we are already waiting for this product to load
     */
    if (loadFullProduct[productId as number] !== undefined) {
      return loadFullProduct[productId as number]
    }

    /**
     * `load` is a cache of all currently pending loads - so if two components both try to load the same product,
     * they will all await the same promise
     */
    if (!loadShallowProduct[productId as number]) {
      const url = `/svc_content/catalog/product${(loadBySKU ? '?sku=' : '/') + productId}`
      loadShallowProduct[productId as number] = rogue.fetch<Product>(url).then(async (data) => {
        // When querying by SKU, svc_content returns a SKU -> Product map...
        if (loadBySKU) {
          data = Object.values(data)[0]
        }
        if (!data) {
          delete loadShallowProduct[productId as number]
          errors.value[productId as number] = true

          return undefined
        }
        prepareProduct(data, rogue)
        setProductData(data)
        delete loadShallowProduct[productId as number]

        return data
      })
        .catch(err => {
          if (!rogue.ssr && err?.status !== 404) {
            rogue.log.error('Error loading single product', err, { productId })
          }
          delete loadShallowProduct[productId as number]
          errors.value[productId as number] = true

          return undefined
        })
    }

    return loadShallowProduct[productId as number]
  }

  /**
   * Reload the inventory status of the product, to see if it's still in stock
   * @returns {Promise<boolean>} Returns True if the inventory has changed, False if it has not
   */
  async function reload(productId: number, full: boolean, rogue: IRogue) {
    await load(productId, rogue)
    const existing = products.value[productId]

    // Dont need to reload for products that are not managed

    let updated = false
    if (existing && (existing.inventory.ms || existing.type === 'simple' || full)) {
      try {
        const data = await rogue.fetch<Product>(`/svc_content/catalog/product/${productId}`)

        prepareProduct(data, rogue)

        if (data.inventory.max !== existing.inventory.max) {
          existing.inventory = data.inventory

          updated = true
        }

        if (full && !deepEqual(existing, data)) {
          updated = true
          setProductData(data)
        }
      } catch (e) {
        rogue.log.info(`Error reloading product ${productId}`, e)
      }
    }

    return updated
  }

  function allChildrenLoaded(productId: number): boolean {
    const notLoaded: number[] = [productId]

    // Check all the children, and the children's children
    while (notLoaded.length > 0) {
      const toCheck = notLoaded.pop()
      // If one of them errored, we dont need to reload
      if (toCheck && !errors.value[toCheck]) {
        const product = products.value[toCheck]
        // The child didn't exist, so we are going to reload the tree
        if (!product) {
          return false
        }

        notLoaded.push(...(product.childrenIds || []))
      }
    }

    return true
  }

  /**
   * Load the product, and all children products.
   * If the products and all children are already loaded, this will return `true` synchronously
   * Otherwise, it will return a promise.
   */
  function loadAllChildren(productId: number, rogue: IRogue) {
    if (loadFullProduct[productId] !== undefined) {
      return loadFullProduct[productId]
    }

    if (allChildrenLoaded(productId)) {
      return true
    }

    loadFullProduct[productId] = rogue.fetch<Product[]>(`/svc_content/catalog/product/complete/${productId}`)
      .then((data) => {
        // The response is a list of products - process them all
        const promises = data
          .map(async product => {
            // The childrenId we receive in this response is better than the one we might have loaded from the `loadSingle` endpoint...
            if (products.value[product.productId]) {
              products.value[product.productId].childrenIds = product.childrenIds
            } else {
              prepareProduct(product, rogue)
              setProductData(product)
            }
          })

        return Promise.all(promises)
      })
      .catch((err) => {
        rogue.log.warn(`Error with loadAll for ${productId}`, err)
        errors.value[productId] = true
      })
      .then(() => delete loadFullProduct[productId])

    return loadFullProduct[productId]
  }

  /**
   * Find the first available parent for a product
   * Example: if productId is "Basic Shirt Green Size Medium", this will return "Basic Shirt Green"
   */
  async function findParent(productId: number, rogue: IRogue) {
    const parent = Object.values(products.value)
      .find(prod => prod.childrenIds && prod.childrenIds?.indexOf(productId) !== -1
        // We dont want to use a bundle as the primary parent for a free product in the bundle...
        && (prod.type !== 'bundle' || prod.bundleOptions?.some(opt => opt.values.some(val => val.productId === productId))))

    if (parent) {
      return parent
    }

    const parentResp = await rogue.fetch<{ [key: number]: Product }>(`/svc_content/catalog/product?childId=${productId}`)
    const parents = Object.values(parentResp)

    const retval = parents.map(parentData => {
      const existing = products.value[parentData.productId]
      if (existing) {
        return existing
      }
      prepareProduct(parentData, rogue)
      setProductData(parentData)

      return parentData
    })

    return retval[0]
  }

  /**
   * Load a list of productIds.
   * If one fails, it does NOT error the full promise
   */
  async function loadAll(productIds: number[] | string[], rogue: IRogue) {
    await Promise.all(productIds.map(id => load(id, rogue)
      .catch(err => rogue.log.info(`Unable to load product ${id}`, err.message))))
  }

  return {
    products,
    errors,
    byUrl,
    setProductData,
    setIdForUrl,
    eraseIdForUrl,
    syncRelated,
    load,
    reload,
    allChildrenLoaded,
    loadAllChildren,
    findParent,
    loadAll,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProductStore, import.meta.hot))
}
